import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { DamFile } from '@stellab/cs-schemas';

import CollectionLink from '@components/ui/CollectionLink';
import Empty from '@components/ui/Empty';
import Icon from '@components/ui/Icon';
import config from '@/config';
import useAccountData from '@hooks/useAccountData';
import { freelyAccessibleText, size, title, transfer, type } from '@intl/generic';
import { getFileById } from '@/services/actions';
import { displayFileSize } from '@utils/formatting';
import { fileIsPArtOfCollection, fileIsTimedTransfer } from '@utils/helpers';

import LoadingOverlay from './LoadingOverlay';
import Pagination from './Pagination';
import { useCollectionData } from '@/hooks/useCollectionData';

type Props = {
  handlePageChange: (page: number) => void;
  total: number;
  data: DamFile[];
  showLegend?: boolean;
  isLoading?: boolean;
};

const FileTable = ({ handlePageChange, total, data, showLegend, isLoading }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);

  const queryClient = useQueryClient();
  const { account } = useAccountData();
  const { collections } = useCollectionData(
    data.map(d => d.meta.collection.id),
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );

  const handleChange = (page: number) => {
    setCurrentPage(page);
    handlePageChange(page);
  };

  const prefetchFileData = (id: string) => {
    queryClient.prefetchQuery({
      queryKey: ['view', id],
      queryFn: () => getFileById({ id }),
      staleTime: 60000,
    });
  };

  const ownedItems = account ? account.filesOwned.filter(f => data.find(d => d.data.id === f)) : [];

  return (
    <>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <LoadingOverlay isLoading={isLoading} />
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 whitespace-nowrap">
            <tr>
              <th scope="col" className="px-6 py-3 w-full">
                <FormattedMessage {...title} />
              </th>

              <th scope="col" className="px-6 py-3 text-center">
                <FormattedMessage {...type} />
              </th>
              <th scope="col" className="px-6 py-3 text-center">
                <FormattedMessage {...size} />
              </th>

              {ownedItems.length > 0 && (
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">
                    <FormattedMessage {...transfer} />
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white">
            {data.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center">
                  <Empty />
                </td>
              </tr>
            )}

            {data.map((item, i) => {
              const isOwnerOfItem = account ? account.filesOwned.includes(item.data.id) : false;
              const itemIsAllowed = account ? account.filesAllowed.includes(item.data.id) : false;
              const isPartOfCollection = fileIsPArtOfCollection(item);

              const freelyAccessible =
                !!(collections.find(c => c.id === item.meta.collection.id)?.downloadPermissionLevel === 'public') ||
                item.data.downloadPermissionLevel === 'public';

              return (
                <tr className="bg-white" key={item.data.id}>
                  <td
                    className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap"
                    onMouseEnter={() => prefetchFileData(item.data.id)}
                  >
                    <Link to={`/view/${item.data.id}`} className="font-bold">
                      {item.data.title}
                    </Link>

                    {fileIsTimedTransfer(item) && <Icon type="faClock" className="text-gray-300 ml-2" />}
                    {isPartOfCollection && <CollectionLink file={item} type="icon" />}
                    {isOwnerOfItem && <Icon type="faUserTie" className="text-gray-300 ml-2" />}
                    {itemIsAllowed && <Icon type="faUserGroup" className="text-gray-300 ml-2" />}
                    {freelyAccessible && <Icon type="faGlobe" className="text-gray-300 ml-2" />}
                  </td>
                  <td className="px-6 py-4 text-center text-nowrap">{item.data.type || 'unknown'}</td>
                  <td className="px-6 py-4 text-center text-nowrap">{displayFileSize(item.data.size)}</td>

                  {ownedItems.length > 0 && (
                    <td className="px-6 py-4 text-right">
                      {isOwnerOfItem && !fileIsTimedTransfer(item) && !isPartOfCollection && (
                        <Link to={`/transfer/file?defaultValue=${item.data.id}`} className="font-medium">
                          <Icon type="faRightLeft" />
                        </Link>
                      )}

                      {isOwnerOfItem && isPartOfCollection && (
                        <Link
                          to={`/transfer/collection?defaultValue=${item.meta.collection.id}`}
                          className="font-medium"
                        >
                          <Icon type="faRightLeft" />
                        </Link>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showLegend && (
        <div className="flex justify-center text-xs mt-4 text-gray-400">
          <div className="flex gap-4 text-nowrap flex-wrap">
            <div>
              <Icon type="faUserTie" className="text-gray-200 mr-2" />
              <FormattedMessage id="table.legend.owner" defaultMessage="Owner" description="Table legend text" />
            </div>

            <div>
              <Icon type="faUserGroup" className="text-gray-200 mr-2" />
              <FormattedMessage
                id="table.legend.sharedWith"
                defaultMessage="Shared with"
                description="Table legend text"
              />
            </div>

            <div>
              <Icon type="faList" className="text-gray-200 mr-2" />
              <FormattedMessage
                id="table.legend.partOfCollection"
                defaultMessage="Part of collection"
                description="Table legend text"
              />
            </div>

            <div>
              <Icon type="faClock" className="text-gray-200 mr-2" />
              <FormattedMessage
                id="table.legend.timedTransfer"
                defaultMessage="Timed Transfer"
                description="Table legend text"
              />
            </div>

            <div>
              <Icon type="faGlobe" className="text-gray-200 mr-2" />
              <FormattedMessage {...freelyAccessibleText} />
            </div>
          </div>
        </div>
      )}

      {total > 0 && (
        <Pagination
          itemsPerPage={config.itemsPerPage}
          total={total}
          currentPage={currentPage}
          handleChange={handleChange}
        />
      )}
    </>
  );
};

export default FileTable;
